import {
  Button,
  Card,
  CellText,
  DataTable,
  EmptyState,
  Icon,
  ListToolbar,
  MapView,
  MoreMenu,
  PageHeader,
  Text,
  useToast,
  compactQuery,
  phoneQuery,
  useMediaQuery,
  type DataTableColumn,
  type MapMarker,
} from '@ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useResetUrlState, useUrlState } from '../../../lib/useUrlState';
import { useSession } from '../../identity/session';
import { useBlooming } from '../../forage/api';
import { useExportItems } from '../../reports/components/ExportButton';
import { useMeta } from '../../system/useMeta';
import { useArchiveLocation, useLocation, useLocations, type LocationListItem } from '../api';
import { ColonyFormDialog } from '../components/ColonyFormDialog';
import { LocationFormDrawer } from '../components/LocationFormDrawer';
import { useRowMenu, useLoadedForAction } from '../../../app/rowActions';
import { mapHeight } from '../mapHeights';
import styles from './LocationsPage.module.css';

type Scope = 'active' | 'archived' | 'all';
const SCOPES = ['active', 'archived', 'all'] as const;
const FILTER_KEYS = ['q', 'status'] as const;

interface Forage {
  now: string[];
  soon: string[];
}

/**
 * Map and table of all locations (LOC-003): where the colonies stand and
 * what blooms there now or soon (TRA-005). Hovering a row highlights its pin.
 */
export function LocationsPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const navigate = useNavigate();
  const meta = useMeta();
  const compact = useMediaQuery(compactQuery);
  // Phones: a low map, so the list starts within the first screen (#60).
  const phone = useMediaQuery(phoneQuery);
  const [scope, setScope] = useUrlState<Scope>('status', 'active', SCOPES);
  const [q, setQ] = useUrlState('q', '');
  const resetFilters = useResetUrlState(FILTER_KEYS);
  const columnSort = useColumnSort();
  const [creating, setCreating] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [action, setAction] = useState<{ kind: 'colony' | 'edit'; id: string } | null>(null);
  const rowMenu = useRowMenu();
  const toast = useToast();
  const archive = useArchiveLocation();
  // Archiving is reversible, so it happens at once with an undo (§6.10).
  const setArchived = (id: string, value: boolean) =>
    archive.mutate(
      { id, archived: value },
      {
        onSuccess: () =>
          toast.show(
            value ? t('apiary.location.archived') : t('apiary.location.unarchived'),
            'success',
            value
              ? {
                  label: t('common.undo'),
                  onClick: () => archive.mutate({ id, archived: false }),
                }
              : undefined,
          ),
        onError: (e) => toast.show(errorMessage(e), 'danger'),
      },
    );
  const locations = useLocations(true);
  const blooming = useBlooming();
  const exportItems = useExportItems('locations');

  const needle = q.trim().toLocaleLowerCase('de');
  const rows = useMemo(
    () =>
      (locations.data ?? [])
        .filter((l) => (scope === 'all' ? true : scope === 'archived' ? l.archived : !l.archived))
        .filter(
          (l) =>
            !needle ||
            [l.name, l.address ?? ''].some((s) => s.toLocaleLowerCase('de').includes(needle)),
        ),
    [locations.data, scope, needle],
  );

  const forage = useMemo(() => {
    const byLocation = new Map<string, Forage>();
    for (const b of blooming.data ?? []) {
      const entry = byLocation.get(b.location.id) ?? { now: [], soon: [] };
      if (b.status === 'blooming') entry.now.push(b.plant.name);
      else if (b.status === 'soon') entry.soon.push(b.plant.name);
      byLocation.set(b.location.id, entry);
    }
    return byLocation;
  }, [blooming.data]);

  const markers: MapMarker[] = useMemo(
    () =>
      rows.map((l) => ({
        id: l.id,
        latitude: l.latitude,
        longitude: l.longitude,
        label: l.name,
        badge: l.active_colonies,
        tone: l.archived || l.active_colonies === 0 ? 'muted' : 'accent',
        onSelect: () => void navigate(`/standorte/${l.id}`),
      })),
    [rows, navigate],
  );

  const forageCell = (l: LocationListItem) => {
    const f = forage.get(l.id);
    const now = f?.now.length ? f.now.join(', ') : null;
    const soon = f?.soon.length
      ? t('apiary.locations.forageSoon', { names: f.soon.join(', ') })
      : null;
    if (compact) return now ? t('apiary.locations.forageNow', { names: now }) : soon;
    if (now) return <CellText main={now} sub={soon} />;
    return soon ?? '–';
  };

  const columns: DataTableColumn<LocationListItem>[] = [
    {
      key: 'name',
      header: t('apiary.locations.columns.name'),
      primary: true,
      cell: (l) => (compact ? l.name : <CellText main={l.name} sub={l.address} />),
      sortValue: (l) => l.name,
    },
    {
      key: 'kind',
      header: t('apiary.locations.columns.kind'),
      cell: (l) => t(`apiary.kind.${l.kind}`),
      sortValue: (l) => t(`apiary.kind.${l.kind}`),
    },
    ...(forage.size > 0
      ? [{ key: 'forage', header: t('apiary.locations.columns.forage'), cell: forageCell }]
      : []),
    {
      key: 'colonies',
      header: t('apiary.locations.columns.colonies'),
      align: 'end',
      cell: (l) =>
        l.archived
          ? t('apiary.locations.archived')
          : t('apiary.locations.colonies', { count: l.active_colonies }),
      sortValue: (l) => (l.archived ? null : l.active_colonies),
    },
  ];

  const filtered = scope !== 'active' || needle !== '';
  const empty = locations.data?.length === 0;

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('apiary.locations.title')}
        actions={
          <>
            {can('locations.write') && (
              <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setCreating(true)}>
                {t('apiary.locations.new')}
              </Button>
            )}
            <MoreMenu
              label={t('common.moreActions')}
              items={[
                {
                  key: 'plants',
                  label: t('forage.plants.title'),
                  onSelect: () => void navigate('/standorte/trachtpflanzen'),
                },
                ...exportItems.map((i, n) => (n === 0 ? { ...i, separated: true } : i)),
              ]}
            />
          </>
        }
      />
      {empty ? (
        <EmptyState
          icon="map-pin"
          title={t('apiary.locations.emptyTitle')}
          actions={
            can('locations.write') && (
              <Button onClick={() => setCreating(true)}>{t('apiary.locations.new')}</Button>
            )
          }
        />
      ) : (
        <>
          <ListToolbar
            sort={sortMenu}
            search={{
              value: q,
              onChange: setQ,
              label: t('common.search'),
              clearLabel: t('common.clear'),
            }}
            scope={{
              label: t('apiary.locations.scope'),
              value: scope,
              onChange: (v) => setScope(v as Scope),
              options: [
                { value: 'active', label: t('apiary.statusFilter.active') },
                { value: 'archived', label: t('apiary.locations.archived') },
                { value: 'all', label: t('apiary.statusFilter.all') },
              ],
            }}
          />
          {meta.data && rows.length > 0 && (
            <MapView
              tileUrl={meta.data.map_tile_url}
              attribution={meta.data.map_attribution}
              markers={markers}
              highlight={hovered}
              height={mapHeight('overview', { phone, compact })}
              label={t('apiary.locations.map')}
              labels={{
                zoomIn: t('apiary.map.zoomIn'),
                zoomOut: t('apiary.map.zoomOut'),
                unavailable: t('apiary.map.unavailable'),
                tilesUnavailable: t('apiary.map.tilesUnavailable'),
                cluster: (count) => t('apiary.map.cluster', { count }),
                fullscreen: t('apiary.map.fullscreen'),
                exitFullscreen: t('apiary.map.exitFullscreen'),
              }}
            />
          )}
          <Card padding="none">
            {locations.error ? (
              <Text tone="danger" role="alert" className={styles.error}>
                {errorMessage(locations.error)}
              </Text>
            ) : (
              <DataTable
                caption={t('apiary.locations.title')}
                columns={columns}
                rows={rows}
                rowKey={(l) => l.id}
                rowHref={(l) => `/standorte/${l.id}`}
                {...columnSort}
                onRowHover={(l) => setHovered(l?.id ?? null)}
                rowActions={rowMenu.table<LocationListItem>(
                  (l) => l.name,
                  (l) => [
                    !l.archived &&
                      can('colonies.write') && {
                        key: 'colony',
                        label: t('apiary.location.newColony'),
                        onSelect: () => setAction({ kind: 'colony', id: l.id }),
                      },
                    can('locations.write') &&
                      rowMenu.edit(() => setAction({ kind: 'edit', id: l.id })),
                    can('locations.write') && {
                      key: 'archive',
                      label: l.archived
                        ? t('apiary.location.unarchive')
                        : t('apiary.location.archive'),
                      separated: true,
                      onSelect: () => setArchived(l.id, !l.archived),
                    },
                  ],
                )}
                loading={locations.isPending}
                empty={
                  filtered && (
                    <EmptyState
                      title={t('common.noResults')}
                      actions={
                        <Button variant="secondary" onClick={resetFilters}>
                          {t('common.resetFilters')}
                        </Button>
                      }
                    />
                  )
                }
              />
            )}
          </Card>
        </>
      )}
      {creating && <LocationFormDrawer open onClose={() => setCreating(false)} />}
      {action?.kind === 'colony' && (
        <ColonyFormDialog open locationId={action.id} onClose={() => setAction(null)} />
      )}
      {action?.kind === 'edit' && <EditLocation id={action.id} onClose={() => setAction(null)} />}
    </div>
  );
}

/** Loads the full location for the edit drawer opened from a row menu. */
function EditLocation({ id, onClose }: { id: string; onClose: () => void }) {
  const location = useLoadedForAction(useLocation(id), onClose);
  return location ? <LocationFormDrawer open location={location} onClose={onClose} /> : null;
}
